/**
 * Date utilities for filtering orders/deliveries by today's date.
 * Use to ensure only current-day data is shown (no historical or future).
 */

/** Get today's date in YYYY-MM-DD format (recomputed each call for correct day) */
export const getTodayYMD = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Normalize date string to YYYY-MM-DD for comparison.
 * Handles: "2025-03-17", "2025-03-17T10:00:00Z", "2025-03-17T00:00:00", etc.
 */
const toYMD = (value: string | null | undefined): string | null => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length < 10) return null;
  return trimmed.slice(0, 10);
};

/**
 * Check if an order's delivery date is today.
 * Checks: delivery_date, deliveryDate, scheduled_date, order_date.
 * If no date field is found, includes the order (assumes today API returned correct data).
 */
/** True when an ISO timestamp falls on the current local calendar day. */
export const isCreatedToday = (iso: string | null | undefined): boolean => {
  const ymd = toYMD(iso);
  if (!ymd) return false;
  return ymd === getTodayYMD();
};

export const isOrderForToday = (order: Record<string, unknown>): boolean => {
  const today = getTodayYMD();
  const dateStr =
    (order.delivery_date as string) ??
    (order.deliveryDate as string) ??
    (order.scheduled_date as string) ??
    (order.order_date as string) ??
    ((order.delivery_address as Record<string, unknown>)?.delivery_date as string);

  const ymd = toYMD(dateStr);
  if (ymd === null) return true; // No date = assume today (from today API)
  return ymd === today;
};
